import { Component, inject, input, OnInit, signal } from '@angular/core';
import { TabComponent } from '../../../components/tab/tab.component';
import { TabLayoutComponent } from '../../../components/tab-layout/tab-layout.component';
import { AccordionComponent } from '../../../components/accordion/accordion.component';
import { APITableComponent } from '../../../components/table/api-table/api-table.component';
import { HttpClient } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { environment } from '../../../../environments/environment';
import { lastValueFrom } from 'rxjs';
import { ExtraBodyParams } from '../../../components/table/table.interface';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { iconoirCheck, iconoirXmark } from '@ng-icons/iconoir';
import { ButtonDirective } from '../../../components/button/button.directive';
import { SnackbarService } from '../../../services/snackbar.service';
import { WiperLoadingComponent } from '../../../components/wiper-loading/wiper-loading.component';
import { ConfigService } from '../../../services/config.service';
import { PlanDraft, PlanDraftResponse } from './plan-draft.interface';
import { RejectionModalComponent } from './rejection-modal/rejection-modal.component';

@Component({
  selector: 'app-draft-tab',
  imports: [
    TabLayoutComponent,
    AccordionComponent,
    DatePipe,
    APITableComponent,
    NgIcon,
    ButtonDirective,
    WiperLoadingComponent,
    RejectionModalComponent,
  ],
  providers: [
    { provide: TabComponent, useExisting: DraftTabComponent },
    provideIcons({ iconoirCheck, iconoirXmark }),
  ],
  templateUrl: './draft-tab.component.html',
  styleUrl: './draft-tab.component.scss',
})
export class DraftTabComponent extends TabComponent implements OnInit {
  private http = inject(HttpClient);
  private snackbar = inject(SnackbarService);
  private configService = inject(ConfigService);

  // Input to control whether action buttons should be shown (supervisor mode)
  showActionButtons = input<boolean>(false);

  override name: string = '';

  public isProcessing = signal<boolean>(false);
  public planDrafts = signal<PlanDraft[]>([]);
  public openedDateIds = signal<Set<string>>(new Set());

  // Rejection modal state
  public isRejectionModalOpen = signal<boolean>(false);
  public currentRejectionDateId = signal<string>('');

  ngOnInit(): void {
    this.name = this.dictionaryService.get('PlanDrafts') || 'Plan Drafts';
    this.main();
  }

  constructor() {
    super();
  }

  public async main(): Promise<void> {
    try {
      this.loading.set(true);
      await this.fetchPlanDrafts();
      this.initialized.set(true);
    } catch (error) {
      console.error('Error loading plan drafts:', error);
      this.snackbar.showError(
        this.dictionaryService.get('FailedToLoadPlanDrafts'),
      );
    } finally {
      this.loading.set(false);
    }
  }

  private async fetchPlanDrafts(): Promise<void> {
    const { from: fromDate, to: toDate } =
      this.configService.getApiDateTimeRange();

    const url = `${environment.apiBaseUrl}/plan-drafts/down-users/dates?from=${fromDate}&to=${toDate}`;

    const response = await lastValueFrom(this.http.get<PlanDraftResponse>(url));

    if (response?.success && response.data?.planDrafts) {
      this.planDrafts.set(response.data.planDrafts);
    }
  }

  getTableEndpoint(dateId: string): string {
    return `/plan-drafts/tables/${dateId}`;
  }

  async approvePlanDraft(dateId: string): Promise<void> {
    if (this.isProcessing()) return;

    try {
      this.isProcessing.set(true);
      const url = `${environment.apiBaseUrl}/plan-drafts/${dateId}/approvals`;
      const response = await lastValueFrom(this.http.put<any>(url, {}));

      if (response?.success) {
        this.snackbar.showSuccess(
          this.dictionaryService.get('PlanDraftApprovedSuccessfully'),
        );
        await this.fetchPlanDrafts();
      }
    } catch (error: any) {
      console.error('Error approving plan draft:', error);
      this.snackbar.showError(
        error?.error?.message ||
          this.dictionaryService.get('FailedToApprovePlanDraft'),
      );
    } finally {
      this.isProcessing.set(false);
    }
  }

  openRejectionModal(dateId: string): void {
    this.currentRejectionDateId.set(dateId);
    this.isRejectionModalOpen.set(true);
  }

  closeRejectionModal(): void {
    this.isRejectionModalOpen.set(false);
    this.currentRejectionDateId.set('');
  }

  async handleRejectionSubmit(message: string): Promise<void> {
    const dateId = this.currentRejectionDateId();
    await this.rejectPlanDraft(dateId, message);
    this.closeRejectionModal();
  }

  async rejectPlanDraft(dateId: string, message: string): Promise<void> {
    if (this.isProcessing()) return;

    try {
      this.isProcessing.set(true);
      const url = `${environment.apiBaseUrl}/plan-drafts/${dateId}/rejections`;
      const response = await lastValueFrom(
        this.http.put<any>(url, { message }),
      );

      if (response?.success) {
        this.snackbar.showSuccess(
          this.dictionaryService.get('PlanDraftRejectedSuccessfully'),
        );
        await this.fetchPlanDrafts();
      }
    } catch (error: any) {
      console.error('Error rejecting plan draft:', error);
      this.snackbar.showError(
        error?.error?.message ||
          this.dictionaryService.get('FailedToRejectPlanDraft'),
      );
    } finally {
      this.isProcessing.set(false);
    }
  }

  onDateAccordionToggled(dateId: string, isOpen: boolean): void {
    if (isOpen) {
      const currentSet = this.openedDateIds();
      if (!currentSet.has(dateId)) {
        const newSet = new Set(currentSet);
        newSet.add(dateId);
        this.openedDateIds.set(newSet);
      }
    }
  }

  isDateOpened(dateId: string): boolean {
    return this.openedDateIds().has(dateId);
  }
}
